import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FileText, ArrowLeft, Scale, AlertTriangle, Shield } from 'lucide-react';
import { Helmet } from 'react-helmet-async';

const TermsOfUsePage = () => {
  const navigate = useNavigate();
  const lastUpdated = 'April 9, 2026';
  const effectiveDate = 'April 9, 2026';

  const sections = [
    {
      id: 'acceptance',
      title: '1. Acceptance of Terms',
      content: `By accessing, browsing, or using the Bulaccino Cafe website located at bulaccinocafe.com (the "Website") or any of our services, you acknowledge that you have read, understood, and agree to be bound by these Terms of Use ("Terms"), our Privacy Policy, and our Cookie Policy, which are incorporated herein by reference.

If you do not agree to these Terms, you must immediately cease all use of the Website and our services. Your continued use of the Website following the posting of any changes to these Terms constitutes acceptance of those changes.

These Terms constitute a legally binding agreement between you ("User," "you," or "your") and Bulaccino Cafe, a business registered and operating under the laws of the Republic of Fiji ("Bulaccino Cafe," "we," "us," or "our").`
    },
    {
      id: 'eligibility',
      title: '2. Eligibility and Account Registration',
      content: `2.1 Age Requirement: You must be at least eighteen (18) years of age to use this Website and our services without parental consent. If you are under eighteen (18) years of age, you may only use this Website with the involvement and consent of a parent or legal guardian.

2.2 Account Creation: Certain features of our Website may require you to create an account. When creating an account, you agree to:
(a) Provide accurate, current, and complete information;
(b) Maintain and promptly update your account information;
(c) Maintain the security and confidentiality of your login credentials;
(d) Accept responsibility for all activities that occur under your account;
(e) Immediately notify us of any unauthorized use of your account.

2.3 Account Termination: We reserve the right to suspend or terminate your account at our sole discretion, without prior notice, for conduct that we determine violates these Terms or is harmful to other users, us, or third parties, or for any other reason.`
    },
    {
      id: 'services',
      title: '3. Description of Services',
      content: `3.1 Bulaccino Cafe provides an online platform for:
(a) Viewing our menu and product offerings;
(b) Placing food and beverage orders for pickup;
(c) Participating in our loyalty rewards program;
(d) Receiving promotional communications and offers;
(e) Accessing information about our cafe and services.

3.2 Service Availability: We strive to ensure our Website and services are available continuously. However, we do not guarantee uninterrupted access and reserve the right to modify, suspend, or discontinue any aspect of our services at any time without notice or liability.

3.3 Order Acceptance: All orders placed through the Website are subject to acceptance by Bulaccino Cafe. We reserve the right to refuse or cancel any order for any reason, including but not limited to product availability, errors in pricing or product information, or suspected fraudulent activity.`
    },
    {
      id: 'ordering',
      title: '4. Ordering and Payment Terms',
      content: `4.1 Pricing: All prices displayed on the Website are in Fiji Dollars (FJD) unless otherwise stated. Prices are subject to change without notice. We make reasonable efforts to ensure pricing accuracy, but errors may occur. In the event of a pricing error, we reserve the right to cancel any orders placed at the incorrect price.

4.2 Payment: Payment for orders must be made at the time of ordering or upon pickup, as specified during the checkout process. We accept cash, major credit cards, debit cards, and approved mobile payment methods.

4.3 Order Modifications and Cancellations: Once an order is confirmed, modifications or cancellations may not be possible. Please review your order carefully before submission. Refunds, if applicable, will be processed in accordance with our refund policy.

4.4 Pickup: Orders must be collected within the specified pickup time. Uncollected orders may be disposed of without refund after a reasonable waiting period.`
    },
    {
      id: 'loyalty',
      title: '5. Loyalty Program Terms',
      content: `5.1 Enrollment: Participation in our loyalty rewards program is voluntary and subject to these Terms and any additional program-specific terms communicated at the time of enrollment.

5.2 Rewards: Rewards earned through the loyalty program have no cash value and cannot be transferred, sold, or exchanged for cash. Rewards may expire if not used within the specified validity period.

5.3 Program Modifications: We reserve the right to modify, suspend, or terminate the loyalty program, including but not limited to reward values, earning rates, and redemption terms, at any time without prior notice.

5.4 Fraud Prevention: We reserve the right to cancel rewards and terminate accounts that we reasonably believe are engaged in fraudulent activity or abuse of the loyalty program.`
    },
    {
      id: 'conduct',
      title: '6. User Conduct and Prohibited Activities',
      content: `6.1 You agree not to engage in any of the following prohibited activities:
(a) Using the Website for any unlawful purpose or in violation of any applicable local, national, or international law;
(b) Attempting to gain unauthorized access to any portion of the Website, other accounts, or computer systems;
(c) Interfering with or disrupting the Website or servers connected to the Website;
(d) Using any robot, spider, or other automated means to access the Website;
(e) Impersonating any person or entity, or falsely stating or misrepresenting your affiliation with any person or entity;
(f) Transmitting any viruses, malware, or other harmful code;
(g) Collecting or harvesting any information from the Website without authorization;
(h) Using the Website to send unsolicited commercial communications;
(i) Engaging in any conduct that restricts or inhibits anyone's use or enjoyment of the Website.

6.2 Violation of these conduct standards may result in immediate termination of your access to the Website and may expose you to civil and/or criminal liability.`
    },
    {
      id: 'intellectual',
      title: '7. Intellectual Property Rights',
      content: `7.1 Ownership: The Website and its entire contents, features, and functionality, including but not limited to all information, text, graphics, logos, images, photographs, videos, audio, software, and the design, selection, and arrangement thereof, are owned by Bulaccino Cafe, its licensors, or other providers and are protected by Fiji and international copyright, trademark, patent, trade secret, and other intellectual property or proprietary rights laws.

7.2 Limited License: Subject to your compliance with these Terms, we grant you a limited, non-exclusive, non-transferable, non-sublicensable license to access and use the Website for your personal, non-commercial use only.

7.3 Restrictions: You shall not:
(a) Copy, modify, distribute, sell, or lease any part of the Website or its content;
(b) Reverse engineer or attempt to extract the source code of the Website;
(c) Remove, alter, or obscure any copyright, trademark, or other proprietary notices;
(d) Use any Bulaccino Cafe trademarks, logos, or branding without prior written consent.`
    },
    {
      id: 'disclaimers',
      title: '8. Disclaimers and Limitation of Liability',
      content: `8.1 Disclaimer of Warranties: THE WEBSITE AND ALL SERVICES ARE PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT ANY WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED. TO THE FULLEST EXTENT PERMITTED BY LAW, BULACCINO CAFE DISCLAIMS ALL WARRANTIES, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.

8.2 Limitation of Liability: TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, IN NO EVENT SHALL BULACCINO CAFE, ITS DIRECTORS, EMPLOYEES, PARTNERS, AGENTS, SUPPLIERS, OR AFFILIATES BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED TO LOSS OF PROFITS, DATA, USE, GOODWILL, OR OTHER INTANGIBLE LOSSES, ARISING OUT OF OR RELATED TO YOUR USE OF OR INABILITY TO USE THE WEBSITE OR SERVICES.

8.3 Maximum Liability: IN NO EVENT SHALL OUR TOTAL LIABILITY TO YOU FOR ALL CLAIMS ARISING OUT OF OR RELATED TO THESE TERMS OR YOUR USE OF THE WEBSITE EXCEED THE AMOUNTS PAID BY YOU TO US, IF ANY, DURING THE SIX (6) MONTHS IMMEDIATELY PRECEDING THE DATE OF THE CLAIM.

8.4 Applicable Law: Some jurisdictions do not allow the exclusion or limitation of certain warranties or liability. In such jurisdictions, our liability shall be limited to the maximum extent permitted by law.`
    },
    {
      id: 'indemnification',
      title: '9. Indemnification',
      content: `You agree to defend, indemnify, and hold harmless Bulaccino Cafe, its officers, directors, employees, agents, licensors, and suppliers from and against any and all claims, damages, obligations, losses, liabilities, costs, and expenses (including but not limited to attorney's fees) arising from:
(a) Your use of and access to the Website;
(b) Your violation of any provision of these Terms;
(c) Your violation of any third-party right, including but not limited to any intellectual property or privacy right;
(d) Any claim that your use of the Website caused damage to a third party.

This indemnification obligation will survive the termination of these Terms and your use of the Website.`
    },
    {
      id: 'governing',
      title: '10. Governing Law and Dispute Resolution',
      content: `10.1 Governing Law: These Terms shall be governed by and construed in accordance with the laws of the Republic of Fiji, without regard to its conflict of law provisions.

10.2 Jurisdiction: You agree that any dispute arising out of or related to these Terms or your use of the Website shall be subject to the exclusive jurisdiction of the courts of Fiji, and you hereby consent to the personal jurisdiction of such courts.

10.3 Dispute Resolution: Prior to initiating any formal legal action, you agree to first attempt to resolve any dispute informally by contacting us at administrator@bulaccinocafe.com. If the dispute is not resolved within thirty (30) days of the initial contact, either party may proceed with formal legal action.

10.4 Class Action Waiver: TO THE EXTENT PERMITTED BY LAW, YOU AGREE THAT ANY CLAIMS RELATING TO THESE TERMS OR THE WEBSITE WILL BE BROUGHT ON AN INDIVIDUAL BASIS ONLY, AND NOT AS A PLAINTIFF OR CLASS MEMBER IN ANY PURPORTED CLASS OR REPRESENTATIVE ACTION.`
    },
    {
      id: 'general',
      title: '11. General Provisions',
      content: `11.1 Entire Agreement: These Terms, together with our Privacy Policy and Cookie Policy, constitute the entire agreement between you and Bulaccino Cafe regarding the Website and supersede all prior agreements and understandings.

11.2 Severability: If any provision of these Terms is found to be unenforceable or invalid, that provision shall be limited or eliminated to the minimum extent necessary, and the remaining provisions shall remain in full force and effect.

11.3 Waiver: No waiver of any term or condition of these Terms shall be deemed a further or continuing waiver of such term or any other term. Our failure to assert any right or provision under these Terms shall not constitute a waiver of such right or provision.

11.4 Assignment: You may not assign or transfer these Terms or any rights hereunder without our prior written consent. We may assign our rights and obligations under these Terms without restriction.

11.5 Notices: We may provide notices to you via the email address associated with your account or by posting on the Website. You may provide notices to us at the contact information provided below.

11.6 Force Majeure: We shall not be liable for any failure or delay in performing our obligations under these Terms due to causes beyond our reasonable control, including but not limited to acts of God, natural disasters, war, terrorism, civil unrest, labor disputes, or government actions.`
    },
    {
      id: 'modifications',
      title: '12. Modifications to Terms',
      content: `We reserve the right to modify these Terms at any time at our sole discretion. If we make material changes to these Terms, we will notify you by posting the updated Terms on the Website and updating the "Last Updated" date. Your continued use of the Website after any such changes constitutes your acceptance of the new Terms.

We encourage you to review these Terms periodically for any changes. Changes are effective immediately upon posting to the Website.`
    },
    {
      id: 'contact',
      title: '13. Contact Information',
      content: `If you have any questions about these Terms of Use, please contact us at:

Bulaccino Cafe
Nadi, Fiji
Email: administrator@bulaccinocafe.com
WhatsApp: +679 675 0102

For general inquiries: administrator@bulaccinocafe.com
For privacy-related matters: administrator@bulaccinocafe.com`
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
      <Helmet>
        <title>Terms of Use | Bulaccino Cafe Nadi, Fiji</title>
        <meta name="description" content="Terms of Use for Bulaccino Cafe website and services. Read our terms and conditions for using our online ordering, loyalty program, and cafe services in Fiji." />
        <link rel="canonical" href="https://bulaccinocafe.com/terms-of-use" />
        <meta name="robots" content="index, follow" />
      </Helmet>
      <main className="pt-8 pb-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8"
          >
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white mb-4 transition-colors"
            >
              <ArrowLeft className="h-4 w-4" />
              Back
            </button>
            
            <div className="flex items-center gap-4 mb-4">
              <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-xl">
                <Scale className="h-8 w-8 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Terms of Use</h1>
                <p className="text-gray-500 dark:text-gray-400">Last updated: {lastUpdated}</p>
              </div>
            </div>
          </motion.div>

          {/* Important Notice */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05 }}
            className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl p-4 mb-6"
          >
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5" />
              <div>
                <h3 className="font-medium text-amber-900 dark:text-amber-100">Important Legal Notice</h3>
                <p className="text-sm text-amber-800 dark:text-amber-200 mt-1">
                  Please read these Terms of Use carefully before using our website. By accessing or using this website, 
                  you agree to be bound by these terms. If you do not agree to all the terms and conditions, you must 
                  not access or use the website.
                </p>
              </div>
            </div>
          </motion.div>

          {/* Table of Contents */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white dark:bg-gray-900 rounded-2xl p-6 md:p-8 shadow-sm border border-gray-200 dark:border-gray-800 mb-6"
          >
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Table of Contents</h2>
            <nav className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {sections.map((section) => (
                <a
                  key={section.id}
                  href={`#${section.id}`}
                  className="text-sm text-teal-600 hover:text-teal-700 dark:text-teal-400 dark:hover:text-teal-300 hover:underline"
                >
                  {section.title}
                </a>
              ))}
            </nav>
          </motion.section>

          {/* Effective Date */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="bg-white dark:bg-gray-900 rounded-2xl p-6 md:p-8 shadow-sm border border-gray-200 dark:border-gray-800 mb-6"
          >
            <div className="flex items-center gap-3 mb-4">
              <FileText className="h-5 w-5 text-gray-400" />
              <span className="text-sm text-gray-600 dark:text-gray-400">
                <strong>Effective Date:</strong> {effectiveDate}
              </span>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              These Terms of Use govern your access to and use of the Bulaccino Cafe website and services. 
              These Terms are governed by the laws of the Republic of Fiji.
            </p>
          </motion.section>

          {/* Terms Sections */}
          {sections.map((section, idx) => (
            <motion.section
              key={section.id}
              id={section.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 + idx * 0.02 }}
              className="bg-white dark:bg-gray-900 rounded-2xl p-6 md:p-8 shadow-sm border border-gray-200 dark:border-gray-800 mb-6"
            >
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">{section.title}</h2>
              <div className="prose prose-gray dark:prose-invert max-w-none text-sm">
                {section.content.split('\n\n').map((paragraph, pIdx) => (
                  <p key={pIdx} className="text-gray-600 dark:text-gray-400 leading-relaxed mb-4 last:mb-0 whitespace-pre-line">
                    {paragraph}
                  </p>
                ))}
              </div>
            </motion.section>
          ))}

          {/* Acknowledgment */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="bg-teal-50 dark:bg-teal-900/20 border border-teal-200 dark:border-teal-800 rounded-xl p-6"
          >
            <div className="flex items-start gap-3">
              <Shield className="h-5 w-5 text-teal-600 mt-0.5" />
              <div>
                <h3 className="font-medium text-teal-900 dark:text-teal-100">Acknowledgment</h3>
                <p className="text-sm text-teal-800 dark:text-teal-200 mt-1">
                  By using this website, you acknowledge that you have read, understood, and agree to be bound by these 
                  Terms of Use. If you are using this website on behalf of an organization, you represent and warrant that 
                  you have the authority to bind that organization to these Terms.
                </p>
              </div>
            </div>
          </motion.section>
        </div>
      </main>
    </div>
  );
};

export default TermsOfUsePage;
